import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

import {
  useAdminAnalyticsAudit,
  type AdminAnalyticsAuditRow,
  type AnalyticsAuditFilters,
} from './api';
import {
  DASH,
  DataTable,
  DataTableShell,
  DateRange,
  FilterSelect,
  Pagination,
  ShortId,
  formatTimestamp,
  toIsoEnd,
  toIsoStart,
} from './shared';

const PAGE_SIZE = 50;

const SOURCE_OPTIONS = [
  { value: 'any', label: 'Any source' },
  { value: 'client', label: 'Client' },
  { value: 'server', label: 'Server' },
  { value: 'legacy_client', label: 'Legacy client' },
];

const FAMILY_OPTIONS = [
  { value: 'any', label: 'Any family' },
  { value: 'landing', label: 'Landing' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'prediction', label: 'Prediction' },
  { value: 'group', label: 'Group' },
  { value: 'arena', label: 'Arena' },
  { value: 'account', label: 'Account' },
  { value: 'manager', label: 'Manager' },
  { value: 'competition', label: 'Competition' },
  { value: 'squad', label: 'Squad' },
  { value: 'error', label: 'Error' },
];

const DELIVERY_OPTIONS = [
  { value: 'any', label: 'Any delivery' },
  { value: 'not_queued', label: 'Not queued' },
  { value: 'pending', label: 'Pending' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
];

export function AnalyticsEventsTab() {
  const [q, setQ] = useState('');
  const [eventName, setEventName] = useState('');
  const [source, setSource] = useState('any');
  const [family, setFamily] = useState('any');
  const [rank, setRank] = useState('');
  const [vendorEligible, setVendorEligible] = useState('any');
  const [deliveryStatus, setDeliveryStatus] = useState('any');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);

  const resetOffset = () => setOffset(0);
  const filters: AnalyticsAuditFilters = {
    q: q.trim() || undefined,
    event_name: eventName.trim() || undefined,
    event_source: source,
    event_family: family,
    funnel_rank: rank.trim() || undefined,
    vendor_eligible: vendorEligible as AnalyticsAuditFilters['vendor_eligible'],
    delivery_status: deliveryStatus,
    from: toIsoStart(from),
    to: toIsoEnd(to),
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, error } = useAdminAnalyticsAudit(filters);
  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/20 p-4 text-sm">
        <p>Event-level collection and server delivery diagnostics. Client / server are collection provenance, not traffic origins. For visitor totals by X, Facebook or direct, use <Link className="text-primary underline" to="/overview">Acquisition origins</Link>.</p>
        <p className="mt-2 text-xs text-muted-foreground">Delivered means the server outbox receiver accepted the event, not confirmation from GA4 or Meta. Browser pixel events are separate. Surface is the recorded application label, not a verified destination hostname.</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px] flex-1">
          <Input
            placeholder="Search event ID, visitor, event name..."
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              resetOffset();
            }}
          />
        </div>
        <label className="flex w-56 flex-col gap-1 text-xs font-medium text-muted-foreground">
          <span>Event name</span>
          <Input
            placeholder="landing_visit"
            value={eventName}
            onChange={(e) => {
              setEventName(e.target.value);
              resetOffset();
            }}
          />
        </label>
        <FilterSelect
          label="Collected by"
          value={source}
          onChange={(v) => {
            setSource(v);
            resetOffset();
          }}
          options={SOURCE_OPTIONS}
        />
        <FilterSelect
          label="Family"
          value={family}
          onChange={(v) => {
            setFamily(v);
            resetOffset();
          }}
          options={FAMILY_OPTIONS}
        />
        <label className="flex w-20 flex-col gap-1 text-xs font-medium text-muted-foreground">
          <span>Rank</span>
          <Input
            placeholder="7"
            value={rank}
            onChange={(e) => {
              setRank(e.target.value);
              resetOffset();
            }}
          />
        </label>
        <FilterSelect
          label="Vendor"
          value={vendorEligible}
          onChange={(v) => {
            setVendorEligible(v);
            resetOffset();
          }}
          options={[
            { value: 'any', label: 'Any' },
            { value: 'true', label: 'Eligible' },
            { value: 'false', label: 'Not eligible' },
          ]}
        />
        <FilterSelect
          label="Delivery"
          value={deliveryStatus}
          onChange={(v) => {
            setDeliveryStatus(v);
            resetOffset();
          }}
          options={DELIVERY_OPTIONS}
        />
        <DateRange
          from={from}
          to={to}
          onChange={({ from: f, to: t }) => {
            setFrom(f);
            setTo(t);
            resetOffset();
          }}
        />
      </div>

      <DataTableShell loading={isLoading} error={error} empty={rows.length === 0}>
        <DataTable<AdminAnalyticsAuditRow>
          rows={rows}
          rowKey={(r) => r.id}
          columns={[
            {
              key: 'occurred_at',
              label: 'Time',
              render: (r) => formatTimestamp(r.occurred_at),
            },
            {
              key: 'event',
              label: 'Event',
              render: (r) => (
                <div className="space-y-1">
                  <div className="font-medium">{r.event_name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    <ShortId value={r.id} />
                  </div>
                </div>
              ),
            },
            {
              key: 'visitor',
              label: 'Visitor',
              render: (r) => <ShortId value={r.visitor_id} />,
            },
            {
              key: 'source',
              label: 'Collected by',
              render: (r) => (
                <div className="flex flex-col gap-1">
                  <Badge variant="outline">{r.event_source}</Badge>
                  <span className="text-xs text-muted-foreground">{r.event_family}</span>
                </div>
              ),
            },
            {
              key: 'surface',
              label: 'Surface / game',
              render: (r) => (
                <div className="text-xs">
                  <div>{r.app_surface || 'Unknown'}</div>
                  <div className="text-muted-foreground">{r.platform || 'Unknown'} · {r.game_key || 'Unknown'}</div>
                </div>
              ),
            },
            {
              key: 'funnel',
              label: 'Funnel',
              render: (r) =>
                r.funnel_rank ? (
                  <span className="text-sm">
                    {r.funnel_rank}. {r.funnel_step ?? r.event_name}
                  </span>
                ) : (
                  DASH
                ),
            },
            {
              key: 'attribution',
              label: 'Attribution',
              render: (r) => (
                <div className="text-xs">
                  <div>{[r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean).join(' / ') || DASH}</div>
                  <div className="text-muted-foreground">
                    {[r.marketing_channel, r.marketing_network, r.marketing_placement]
                      .filter(Boolean)
                      .join(' / ') ||
                      (r.click_id_source ? `${r.click_id_source} present` : r.referrer || DASH)}
                  </div>
                </div>
              ),
            },
            {
              key: 'eligibility',
              label: 'Eligibility',
              render: (r) => (
                <div className="text-xs">
                  <div>{r.vendor_eligible ? 'Vendor eligible' : 'Admin only'}</div>
                  <div className="text-muted-foreground">{r.consent_state ?? DASH}</div>
                </div>
              ),
            },
            {
              key: 'delivery',
              label: 'Server delivery',
              render: (r) => (
                <div className="text-xs">
                  <Badge variant={r.delivery_status === 'failed' ? 'destructive' : 'outline'}>
                    {r.delivery_status}
                  </Badge>
                  <div className="mt-1 text-muted-foreground">
                    {r.delivery_attempts ? `${r.delivery_attempts} attempts` : DASH}
                  </div>
                </div>
              ),
            },
          ]}
        />
        <Pagination
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          onChange={(next) => setOffset(next)}
        />
      </DataTableShell>
    </div>
  );
}
